/**
 * WorkerConnection: ORM Connection adapter that delegates to db-bridge (wa-sqlite + OPFS).
 */

import { dbBridge } from './worker/db-bridge';
import type { BulkUpsertOfflineEntitiesPayload } from './worker/types';

export interface ConnectionOptions {
  database: string;
  version?: number;
  encrypted?: boolean;
  readOnly?: boolean;
}

class WorkerConnection {
  private _isInitialized = false;

  /**
   * Deve combaciare con lo stato reale del bridge: dopo `dbBridge.fullReset()` il worker
   * non è più inizializzato anche se questo flag non era ancora stato azzerato da `close()`.
   */
  get isInitialized(): boolean {
    return this._isInitialized && dbBridge.isInitialized;
  }

  async initialize(options?: ConnectionOptions): Promise<void> {
    const dbName = options?.database ?? 'b2b_offline';
    if (this._isInitialized && !dbBridge.isInitialized) {
      this._isInitialized = false;
    }
    if (this._isInitialized) return;
    await dbBridge.initialize(dbName);
    this._isInitialized = true;
  }

  async close(): Promise<void> {
    this._isInitialized = false;
    await dbBridge.close();
  }

  async execute(sql: string): Promise<void> {
    this._ensureInitialized();
    await dbBridge.execute(sql);
  }

  async run(sql: string, params: unknown[] = []): Promise<{ changes: number; lastId: number }> {
    this._ensureInitialized();
    const result = await dbBridge.run(sql, params);
    return { changes: result.changes, lastId: result.lastInsertRowId };
  }

  async query<T = Record<string, unknown>>(sql: string, params: unknown[] = []): Promise<T[]> {
    this._ensureInitialized();
    return dbBridge.query<T>(sql, params);
  }

  async executeSet(
    statements: Array<{ statement: string; values: unknown[] }>,
    options?: { inTransaction?: boolean }
  ): Promise<void> {
    this._ensureInitialized();
    const batchStatements = statements.map((s) => ({
      sql: s.statement,
      params: s.values ?? [],
    }));
    const useTransaction = options?.inTransaction !== true;
    await dbBridge.batch(batchStatements, { useTransaction });
  }

  async bulkUpsertOfflineEntities(payload: BulkUpsertOfflineEntitiesPayload): Promise<void> {
    this._ensureInitialized();
    await dbBridge.bulkUpsertOfflineEntities(payload);
  }

  async transaction<T>(callback: () => Promise<T>): Promise<T> {
    this._ensureInitialized();
    return dbBridge.transaction(callback);
  }

  private _ensureInitialized(): void {
    if (!this._isInitialized) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
  }
}

export const db = new WorkerConnection();
